// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import {
  createEmote,
  deleteEmote,
  editEmote,
  getEmoteDetail,
  getEmoteList,
  toggleEmoteActive,
} from '@/modules/emote/emote.service'
import type {
  TCreateEmoteRequest,
  TEditEmoteRequest,
  TEmote,
  TToggleEmoteActiveRequest,
} from '@/modules/emote/emote.types'

export const emoteListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['emoteList', params],
    queryFn: () => getEmoteList(params),
  })

export const emoteDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['emoteDetail', id],
    queryFn: () => getEmoteDetail(id),
    staleTime: 0,
  })

export function useCreateEmote({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TCreateEmoteRequest>) {
  return useMutation<void, TResponseError, TCreateEmoteRequest>({
    mutationFn: createEmote,
    onSuccess,
    onError,
  })
}

export function useEditEmote({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TEditEmoteRequest>) {
  return useMutation<void, TResponseError, TEditEmoteRequest>({
    mutationFn: editEmote,
    onSuccess,
    onError,
  })
}

export function useToggleEmoteActive({
  onSuccess,
  onError,
}: TMutation<TEmote, TResponseError, TToggleEmoteActiveRequest>) {
  return useMutation<TEmote, TResponseError, TToggleEmoteActiveRequest>({
    mutationFn: toggleEmoteActive,
    onSuccess,
    onError,
  })
}

export function useDeleteEmote({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deleteEmote,
    onSuccess,
    onError,
  })
}
